import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { CardOrderingService, CardOrderingRequest, RetailersService, Int64ComboValueResponse } from '@src/app/shared/backend/clients';
import { FilterOperator } from '@app/shared/enums/FilterOperator.enum';
import { ComboData } from '@src/app/shared/components/comboData/comboData';
import { BaseViewComponent } from '@src/app/shared/components/base-list/base-view.component';

@Component({
    selector: 'app-add-card-order',
    templateUrl: './add-card-order.component.html',
    styleUrl: './add-card-order.component.scss',
    host: {
        class: 'app-add-card-order'
    },
    standalone: false
})
export class AddCardOrderComponent extends BaseViewComponent  implements OnInit {

  combosData: { [key: string]: ComboData } = {
    retailers: new ComboData(async (filter) => await firstValueFrom(this.retailersService.apiRetailersFilterComboPost(filter)))
  };

  destinationStore: Int64ComboValueResponse = {};
  destinationStoresList: any;
  destinationStoresTotalRecords: any;
  destinationStoresRowsPerPageOption: number = 10;

  destinationStoresMap: { [key: number]: Int64ComboValueResponse[] } = {};
  destinationStoresTotalRecordsMap: { [key: number]: number } = {};

  limit = 100;
  public serialNumbersArray = new FormArray([new FormControl('', Validators.required)]);

  public cardOrderingForm: FormGroup = this.fb.group({
    originRetailer: [[], [Validators.required]],
    destinations: this.fb.array([])
  });

  constructor(
    private router: Router,
    private retailersService: RetailersService,
    private cardOrderingService: CardOrderingService,
    private fb: FormBuilder
  ) { super(); }

  ngOnInit() {
    this.init();
  }

  private async init(): Promise<void> {
    await this.initCombosData();
  }

  get destinations() {
    return this.cardOrderingForm.get('destinations') as FormArray;
  }

  createDestinationFormGroup(): FormGroup {
    return this.fb.group({
      destinationRetailer: [[], Validators.required],
      serialNumbers: this.fb.array([
        new FormControl('', Validators.required)
      ])
    });
  }

  async getDestinationStores(event: any, destinationIndex?: number) {
    const filters: any[] = [];
    const search = event?.search?.trim() || null;
    const activePage = Math.max(1, (event?.pageIndex ?? 1));
    if (search) {
      filters.push({
        propertyName: 'name',
        operator: FilterOperator.CONTAINS,
        values: [search]
      });
    }
    
    filters.push({
      propertyName: 'isActive',
      operator: 'eq',
      values: ['true']
    });

    const filter = {
      pageNumber: activePage,
      pageSize: event?.pageSize ?? this.destinationStoresRowsPerPageOption,
      additionalFilters: filters
    };
 
    const destinationStoresPage = await firstValueFrom(this.retailersService.apiRetailersFilterComboPost(filter));

    if (destinationIndex !== undefined) {
      this.destinationStoresMap[destinationIndex] = destinationStoresPage.results ?? [];
      this.destinationStoresTotalRecordsMap[destinationIndex] = destinationStoresPage.totalCount ?? 0;
    } else {
      this.destinationStoresList = destinationStoresPage.results ?? [];
      this.destinationStoresTotalRecords = destinationStoresPage.totalCount ?? 0;
    }
  }

  getDestinationStoresTotalRecords(destinationIndex: number): number {
    return this.destinationStoresTotalRecordsMap[destinationIndex] ?? 0;
  }

  getDestinationStoresTotalPages(destinationIndex: number): number {
    const totalRecords = this.getDestinationStoresTotalRecords(destinationIndex);
    const pageSize = this.destinationStoresRowsPerPageOption || 1;

    return Math.max(1, Math.ceil(totalRecords / pageSize));
  }

  getStoresList(destinationIndex: number): any[] {
    return this.destinationStoresMap[destinationIndex] || [];
  }

  async addDestinationRetailer() {
    if (this.cardOrderingForm.get('originRetailer')?.valid) {
      const destinationGroup = this.createDestinationFormGroup();
      const newIndex = this.destinations.length;
      this.destinations.push(destinationGroup);

      // Load stores specifically for this new destination
      await this.getDestinationStores(
        { pageIndex: 1, pageSize: this.destinationStoresRowsPerPageOption, search: '' },
        newIndex
      );

      const destinationControl = this.destinations.at(newIndex);
      destinationControl.get('destinationRetailer')?.setValidators([Validators.required]);
      destinationControl.get('serialNumbers')?.setValidators([Validators.required]);
    }
  }

  removeDestination(index: number) {
    this.destinations.removeAt(index);
    delete this.destinationStoresMap[index];
    delete this.destinationStoresTotalRecordsMap[index];
  }

  onLazyLoadDestinationStores(event: any, destinationIndex: number) {
    this.getDestinationStores(event, destinationIndex);
  }

  private async initCombosData(): Promise<void> {
    await Promise.all([
      this.combosData['retailers'].getData(null, 1)
    ]);
  }

  async onLazyLoadComboData(comboType: 'retailers', event: any) {
    const search = event?.search?.trim() || null;
    const activePage = Math.max(1, (event?.pageIndex ?? 1));

    await this.combosData[comboType].getData(search, activePage);
  }

  getComboTotalPages(comboType: 'retailers' ): number {
    const totalRecords = this.combosData[comboType]?.totalRecords ?? 0;
    const pageSize = Number(this.combosData[comboType]?.rowsPerPageOption) || 5;

    return Math.max(1, Math.ceil(totalRecords / pageSize));
  }

  getSerialNumbersForDestination(destinationIndex: number): FormArray {
    return this.destinations.at(destinationIndex).get('serialNumbers') as FormArray;
  }

  addSerialNumber(destinationIndex: number) {
    const serialNumbers = this.getSerialNumbersForDestination(destinationIndex);
    if (serialNumbers.length < this.limit) {
      serialNumbers.push(new FormControl('', Validators.required));
    }
  }

  removeSerialNumber(destinationIndex: number, serialNumberIndex: number) {
    const serialNumbers = this.getSerialNumbersForDestination(destinationIndex);
    if (serialNumbers.length > 1) {
      serialNumbers.removeAt(serialNumberIndex);
    }
  }

  async submitForm() {
    if (this.cardOrderingForm.valid) {
      const formValue = this.cardOrderingForm.value;
      const itemList: { [key: string]: string[] } = {};

      formValue.destinations.forEach((destination: any) => {
        const retailerId = destination.destinationRetailer?.[0];

        if (retailerId != null) {
          itemList[String(retailerId)] = destination.serialNumbers;
        }
      });
      const payload: CardOrderingRequest = {
        retailerId: Number(formValue.originRetailer?.[0]),
        itemList
      };

      await firstValueFrom(this.cardOrderingService.apiCardOrderingCreatePost(payload));

      this.router.navigate(['/card-orders']);

    }
  }
}
